import { RegisterDeviceRequest } from '../types';

export class DeviceDetector {
  /**
   * Generate a unique device ID based on browser fingerprint
   */
  static generateDeviceId(): string {
    const stored = localStorage.getItem('deviceId');
    if (stored) return stored;

    // Create a simple fingerprint
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    let fingerprint = '';
    
    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Device fingerprint', 2, 2);
      fingerprint = canvas.toDataURL();
    }

    const data = [
      navigator.userAgent,
      navigator.language,
      window.screen.width,
      window.screen.height,
      window.screen.colorDepth,
      new Date().getTimezoneOffset(),
      fingerprint
    ].join('|');

    // Simple hash function
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
      const char = data.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }

    const deviceId = `device_${Math.abs(hash).toString(36)}_${Date.now().toString(36)}`;
    localStorage.setItem('deviceId', deviceId);
    return deviceId;
  }

  /**
   * Detect device type (mobile, desktop, tablet)
   */
  static getDeviceType(): 'mobile' | 'desktop' | 'tablet' {
    const ua = navigator.userAgent.toLowerCase();
    
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      return 'tablet';
    }
    
    if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
      return 'mobile';
    }
    
    return 'desktop';
  }

  /**
   * Parse user agent to get OS info
   */
  static getOSInfo(): { osName: string; osVersion: string } {
    const ua = navigator.userAgent;
    let osName = 'Unknown';
    let osVersion = '';

    // Windows
    if (ua.indexOf('Windows NT 10.0') !== -1) { osName = 'Windows'; osVersion = '10'; }
    else if (ua.indexOf('Windows NT 6.3') !== -1) { osName = 'Windows'; osVersion = '8.1'; }
    else if (ua.indexOf('Windows NT 6.2') !== -1) { osName = 'Windows'; osVersion = '8'; }
    else if (ua.indexOf('Windows NT 6.1') !== -1) { osName = 'Windows'; osVersion = '7'; }
    else if (ua.indexOf('Windows NT 6.0') !== -1) { osName = 'Windows'; osVersion = 'Vista'; }
    else if (ua.indexOf('Windows NT 5.1') !== -1) { osName = 'Windows'; osVersion = 'XP'; }
    else if (ua.indexOf('Windows') !== -1) { osName = 'Windows'; }
    
    // macOS
    else if (ua.indexOf('Mac OS X') !== -1) {
      osName = 'macOS';
      const match = ua.match(/Mac OS X ([\d_]+)/);
      if (match) osVersion = match[1].replace(/_/g, '.');
    }
    
    // iOS
    else if (ua.indexOf('iPhone') !== -1 || ua.indexOf('iPad') !== -1) {
      osName = 'iOS';
      const match = ua.match(/OS ([\d_]+)/);
      if (match) osVersion = match[1].replace(/_/g, '.');
    }
    
    // Android
    else if (ua.indexOf('Android') !== -1) {
      osName = 'Android';
      const match = ua.match(/Android ([\d.]+)/);
      if (match) osVersion = match[1];
    }
    
    // Linux
    else if (ua.indexOf('Linux') !== -1) {
      osName = 'Linux';
    }

    return { osName, osVersion };
  }

  /**
   * Parse user agent to get browser info
   */
  static getBrowserInfo(): { browserName: string; browserVersion: string } {
    const ua = navigator.userAgent;
    let browserName = 'Unknown';
    let browserVersion = '';

    // Edge
    if (ua.indexOf('Edg/') !== -1) {
      browserName = 'Edge';
      const match = ua.match(/Edg\/([\d.]+)/);
      if (match) browserVersion = match[1];
    }
    // Chrome
    else if (ua.indexOf('Chrome') !== -1 && ua.indexOf('Edg') === -1) {
      browserName = 'Chrome';
      const match = ua.match(/Chrome\/([\d.]+)/);
      if (match) browserVersion = match[1];
    }
    // Safari
    else if (ua.indexOf('Safari') !== -1 && ua.indexOf('Chrome') === -1) {
      browserName = 'Safari';
      const match = ua.match(/Version\/([\d.]+)/);
      if (match) browserVersion = match[1];
    }
    // Firefox
    else if (ua.indexOf('Firefox') !== -1) {
      browserName = 'Firefox';
      const match = ua.match(/Firefox\/([\d.]+)/);
      if (match) browserVersion = match[1];
    }
    // Opera
    else if (ua.indexOf('OPR') !== -1) {
      browserName = 'Opera';
      const match = ua.match(/OPR\/([\d.]+)/);
      if (match) browserVersion = match[1];
    }
    // IE
    else if (ua.indexOf('Trident') !== -1 || ua.indexOf('MSIE') !== -1) {
      browserName = 'Internet Explorer';
      const match = ua.match(/(?:MSIE |rv:)([\d.]+)/);
      if (match) browserVersion = match[1];
    }

    return { browserName, browserVersion };
  }

  /**
   * Get friendly device name
   */
  static getDeviceName(): string {
    const { osName } = this.getOSInfo();
    const { browserName } = this.getBrowserInfo();
    const deviceType = this.getDeviceType();
    
    // Try to get model name from user agent
    const ua = navigator.userAgent;
    
    // iPhone models
    if (ua.indexOf('iPhone') !== -1) {
      return `iPhone (${browserName})`;
    }
    
    // iPad models
    if (ua.indexOf('iPad') !== -1) {
      return `iPad (${browserName})`;
    }
    
    // Android devices
    if (ua.indexOf('Android') !== -1) {
      const match = ua.match(/Android.*;\s([^)]+)\)/);
      if (match && match[1]) {
        return match[1].trim();
      }
      return `Android ${deviceType} (${browserName})`;
    }
    
    // Desktop
    return `${osName} ${deviceType} (${browserName})`;
  }

  /**
   * Collect all device info for login
   */
  static getDeviceInfo(): RegisterDeviceRequest {
    const { osName, osVersion } = this.getOSInfo();
    const { browserName, browserVersion } = this.getBrowserInfo();
    
    return {
      deviceId: this.generateDeviceId(),
      deviceName: this.getDeviceName(),
      deviceType: this.getDeviceType(),
      osName,
      osVersion,
      browserName,
      browserVersion,
      userAgent: navigator.userAgent,
      appVersion: process.env.REACT_APP_VERSION || '1.0.0'
    };
  }
}

export default DeviceDetector;
